// Pixelwand im Browser: Leinwand zeichnen (Canvas), verschieben und zoomen, Pixel setzen,
// Mauszeiger der anderen zeigen und im Kreativ-Modus das Zielbild samt Fortschritt.

import {
  EMPTY, GOAL, PALETTE, PALETTE_NAMES, clamp,
  type ClientMsg, type Mode, type ServerMsg, type TargetInfo,
} from '../shared/protocol.ts';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const stage = $<HTMLElement>('stage');
const view = $<HTMLCanvasElement>('view');
const ctx = view.getContext('2d')!;
const goalCard = $<HTMLElement>('goal');
const goalImg = $<HTMLCanvasElement>('goal-img');
const paletteEl = $<HTMLElement>('palette');
const toastEl = $<HTMLElement>('toast');
const nameInput = $<HTMLInputElement>('name');

// ---------- Zustand ----------

const store = {
  get: (k: string) => { try { return localStorage.getItem(`pixelwand-${k}`); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(`pixelwand-${k}`, v); } catch { /* privates Fenster */ } },
};

const modeFromHash = (): Mode => (location.hash === '#kreativ' ? 'creative' : 'free');
let mode: Mode = modeFromHash();
let me = '';
let W = 0;
let H = 0;
let board = new Uint8Array(0);
let target: TargetInfo | null = null;
let locked = false;
let selected = clamp(Number(store.get('color') ?? 5) || 0, 0, PALETTE.length - 1);
let hints = store.get('hints') !== '0';

const ANIMALS = ['Murmeli', 'Steinbock', 'Gämse', 'Luchs', 'Fuchs', 'Dachs', 'Igel', 'Biber', 'Adler', 'Eule', 'Specht', 'Hase'];
let myName = store.get('name') || `${ANIMALS[Math.floor(Math.random() * ANIMALS.length)]} ${Math.floor(Math.random() * 90) + 10}`;
store.set('name', myName);
nameInput.value = myName;

// Ansicht: Grösse eines Pixels in CSS-Pixeln und Lage der Leinwand im Bühnenbereich
let scale = 1;
let ox = 0;
let oy = 0;
let fitScale = 1;
let hover: { x: number; y: number } | null = null;
/** Darunter setzt ein Klick keinen Pixel, sondern zoomt hinein */
const MIN_PLACE_SCALE = 5;

interface Cursor { name: string; color: string; x: number; y: number; tx: number; ty: number }
const cursors = new Map<string, Cursor>();
const pops: { x: number; y: number; c: number; t: number }[] = [];

// Offscreen: die Leinwand in Originalgrösse, ein Pixel = ein Canvas-Pixel
const boardCv = document.createElement('canvas');
const bctx = boardCv.getContext('2d')!;

// ---------- Leinwand zeichnen ----------

const rgb = PALETTE.map(h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)));

function paintAll() {
  boardCv.width = W;
  boardCv.height = H;
  const img = bctx.createImageData(W, H);
  for (let i = 0; i < board.length; i++) {
    const c = board[i];
    if (c === EMPTY) continue; // durchsichtig, darunter liegt das Karomuster
    const [r, g, b] = rgb[c];
    img.data.set([r, g, b, 255], i * 4);
  }
  bctx.putImageData(img, 0, 0);
}

function paint(x: number, y: number) {
  const c = board[y * W + x];
  if (c === EMPTY) bctx.clearRect(x, y, 1, 1);
  else { bctx.fillStyle = PALETTE[c]; bctx.fillRect(x, y, 1, 1); }
}

let checker: CanvasPattern | null = null;
function checkerPattern() {
  if (checker) return checker;
  const c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d')!;
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, 16, 16);
  g.fillStyle = '#ebebeb'; g.fillRect(0, 0, 8, 8); g.fillRect(8, 8, 8, 8);
  return (checker = ctx.createPattern(c, 'repeat')!);
}

function drawArrow(x: number, y: number, color: string, name: string) {
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.beginPath();
  ctx.moveTo(0, 0); ctx.lineTo(0, 16); ctx.lineTo(4.5, 12); ctx.lineTo(7.5, 18.5);
  ctx.lineTo(10, 17.5); ctx.lineTo(7, 11); ctx.lineTo(12, 11); ctx.closePath();
  ctx.fillStyle = color; ctx.fill();
  ctx.lineWidth = 1.5; ctx.strokeStyle = '#fff'; ctx.stroke();
  ctx.font = '600 12px system-ui, sans-serif';
  const w = ctx.measureText(name).width + 12;
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.roundRect(12, 17, w, 20, 10); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
  ctx.fillText(name, 18, 27.5);
  ctx.restore();
}

function render(now: number) {
  requestAnimationFrame(render);
  const dpr = window.devicePixelRatio || 1;
  const cw = stage.clientWidth;
  const ch = stage.clientHeight;
  if (view.width !== Math.round(cw * dpr) || view.height !== Math.round(ch * dpr)) {
    view.width = Math.round(cw * dpr);
    view.height = Math.round(ch * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cw, ch);
  if (!W) return;

  const bw = W * scale;
  const bh = H * scale;
  // Leinwand mit leichtem Schatten, leere Pixel als Karomuster
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,.18)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = checkerPattern();
  ctx.fillRect(ox, oy, bw, bh);
  ctx.restore();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(boardCv, ox, oy, bw, bh);

  // Raster ab einer gewissen Zoomstufe, nur im sichtbaren Teil
  if (scale >= 10) {
    const x0 = Math.max(0, Math.floor(-ox / scale)), x1 = Math.min(W, Math.ceil((cw - ox) / scale));
    const y0 = Math.max(0, Math.floor(-oy / scale)), y1 = Math.min(H, Math.ceil((ch - oy) / scale));
    ctx.beginPath();
    for (let x = x0; x <= x1; x++) { ctx.moveTo(Math.round(ox + x * scale) + .5, oy + y0 * scale); ctx.lineTo(Math.round(ox + x * scale) + .5, oy + y1 * scale); }
    for (let y = y0; y <= y1; y++) { ctx.moveTo(ox + x0 * scale, Math.round(oy + y * scale) + .5); ctx.lineTo(ox + x1 * scale, Math.round(oy + y * scale) + .5); }
    ctx.strokeStyle = 'rgba(0,0,0,.08)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Kreativ-Modus: Hilfspunkt in der Farbe des Zielbilds, wo der Pixel noch nicht stimmt
  if (target && hints) {
    const d = Math.max(3, scale * .34);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0,0,0,.45)';
    for (let i = 0; i < target.cells.length; i++) {
      const t = target.cells[i];
      if (t === EMPTY || board[i] === t) continue;
      const x = ox + (i % W + .5) * scale - d / 2;
      const y = oy + (Math.floor(i / W) + .5) * scale - d / 2;
      ctx.fillStyle = PALETTE[t];
      ctx.fillRect(x, y, d, d);
      ctx.strokeRect(x + .5, y + .5, d - 1, d - 1);
    }
  }

  // Kurzes Aufblitzen bei neuen Pixeln
  for (let i = pops.length - 1; i >= 0; i--) {
    const p = pops[i];
    const k = (now - p.t) / 350;
    if (k >= 1) { pops.splice(i, 1); continue; }
    const grow = scale * (1 + k * .8);
    ctx.globalAlpha = 1 - k;
    ctx.strokeStyle = PALETTE[p.c] === '#FFFFFF' ? '#888' : PALETTE[p.c];
    ctx.lineWidth = 2;
    ctx.strokeRect(ox + (p.x + .5) * scale - grow / 2, oy + (p.y + .5) * scale - grow / 2, grow, grow);
    ctx.globalAlpha = 1;
  }

  // Vorschau des Pixels unter der Maus
  if (hover) {
    const x = ox + hover.x * scale;
    const y = oy + hover.y * scale;
    ctx.globalAlpha = .6;
    ctx.fillStyle = PALETTE[selected];
    ctx.fillRect(x, y, scale, scale);
    ctx.globalAlpha = 1;
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#1d1d1b';
    ctx.strokeRect(x - 1, y - 1, scale + 2, scale + 2);
  }

  // Mauszeiger der anderen, weich nachgeführt
  for (const c of cursors.values()) {
    c.x += (c.tx - c.x) * .3;
    c.y += (c.ty - c.y) * .3;
    drawArrow(ox + c.x * scale, oy + c.y * scale, c.color, c.name);
  }
}
requestAnimationFrame(render);

// ---------- Ansicht: einpassen, zoomen, verschieben ----------

function freeArea() {
  const s = stage.getBoundingClientRect();
  let top = 16, right = 16, left = 16;
  const bottom = s.bottom - paletteEl.getBoundingClientRect().top + 16;
  if (!goalCard.hidden) {
    const g = goalCard.getBoundingClientRect();
    if (g.width > s.width * .6) top = g.bottom - s.top + 12;
    else right = s.right - g.left + 16;
  }
  if (s.width < 500) left = right = 8;
  return { x: left, y: top, w: s.width - left - right, h: s.height - top - bottom };
}

function fit() {
  const a = freeArea();
  fitScale = Math.max(.5, Math.min(a.w / W, a.h / H));
  // Bei kleinen Bildern ganze Pixel, damit das Raster sauber ist
  scale = fitScale >= 4 ? Math.floor(fitScale) : fitScale;
  ox = Math.round(a.x + (a.w - W * scale) / 2);
  oy = Math.round(a.y + (a.h - H * scale) / 2);
}

function zoomAt(factor: number, sx: number, sy: number) {
  const next = clamp(scale * factor, Math.min(fitScale * .5, 1), 64);
  const k = next / scale;
  ox = sx - (sx - ox) * k;
  oy = sy - (sy - oy) * k;
  scale = next;
}

const cellAt = (sx: number, sy: number) => {
  const x = Math.floor((sx - ox) / scale);
  const y = Math.floor((sy - oy) / scale);
  return x >= 0 && y >= 0 && x < W && y < H ? { x, y } : null;
};

// ---------- Eingabe ----------

const local = (e: { clientX: number; clientY: number }) => {
  const r = stage.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
};

const pointers = new Map<number, { x: number; y: number }>();
type Gesture =
  | { kind: 'tap' | 'pan'; sx: number; sy: number; ox0: number; oy0: number; button: number }
  | { kind: 'pinch'; dist0: number; scale0: number; mx: number; my: number; ox0: number; oy0: number };
let gesture: Gesture | null = null;

function startPan(p: { x: number; y: number }, kind: 'tap' | 'pan', button = 0) {
  gesture = { kind, sx: p.x, sy: p.y, ox0: ox, oy0: oy, button };
}

view.addEventListener('pointerdown', e => {
  view.setPointerCapture(e.pointerId);
  const p = local(e);
  pointers.set(e.pointerId, p);
  if (pointers.size === 1) startPan(p, 'tap', e.button);
  else if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    gesture = {
      kind: 'pinch', dist0: Math.hypot(a.x - b.x, a.y - b.y) || 1, scale0: scale,
      mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, ox0: ox, oy0: oy,
    };
    hover = null;
  }
});

view.addEventListener('pointermove', e => {
  const p = local(e);
  if (pointers.has(e.pointerId)) pointers.set(e.pointerId, p);
  if (gesture?.kind === 'tap' && Math.hypot(p.x - gesture.sx, p.y - gesture.sy) > 6) {
    gesture.kind = 'pan';
    view.classList.add('panning');
  }
  if (gesture?.kind === 'pan') {
    ox = gesture.ox0 + p.x - gesture.sx;
    oy = gesture.oy0 + p.y - gesture.sy;
  } else if (gesture?.kind === 'pinch' && pointers.size >= 2) {
    const [a, b] = [...pointers.values()];
    const next = clamp(gesture.scale0 * Math.hypot(a.x - b.x, a.y - b.y) / gesture.dist0, Math.min(fitScale * .5, 1), 64);
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const k = next / gesture.scale0;
    ox = mx - (gesture.mx - gesture.ox0) * k;
    oy = my - (gesture.my - gesture.oy0) * k;
    scale = next;
  }
  if (e.pointerType === 'mouse') {
    hover = gesture?.kind === 'pan' || scale < MIN_PLACE_SCALE ? null : cellAt(p.x, p.y);
    view.classList.toggle('zoom-in', scale < MIN_PLACE_SCALE && !!cellAt(p.x, p.y));
    showCoords();
    sendCursor((p.x - ox) / scale, (p.y - oy) / scale);
  }
});

function endPointer(e: PointerEvent) {
  const p = local(e);
  if (gesture?.kind === 'tap' && gesture.button === 0 && pointers.size === 1) {
    const cell = cellAt(p.x, p.y);
    // Zu weit weg, um einen Pixel sicher zu treffen: erst hineinzoomen
    if (cell && scale < MIN_PLACE_SCALE) zoomAt(16 / scale, p.x, p.y);
    else if (cell) place(cell.x, cell.y);
  }
  pointers.delete(e.pointerId);
  view.classList.remove('panning');
  if (pointers.size === 0) gesture = null;
  // Nach dem Zoomen mit zwei Fingern wird mit dem verbleibenden nur verschoben, nie gesetzt
  else if (pointers.size === 1) startPan([...pointers.values()][0], 'pan');
}
view.addEventListener('pointerup', endPointer);
view.addEventListener('pointercancel', e => { pointers.delete(e.pointerId); if (!pointers.size) gesture = null; });
view.addEventListener('pointerleave', e => {
  if (e.pointerType !== 'mouse') return;
  hover = null;
  showCoords();
  sendCursor(-1, -1);
});
view.addEventListener('contextmenu', e => e.preventDefault());
view.addEventListener('wheel', e => {
  e.preventDefault();
  const p = local(e);
  zoomAt(Math.exp(-e.deltaY * (e.deltaMode === 1 ? .05 : .0018)), p.x, p.y);
  hover = scale < MIN_PLACE_SCALE ? null : cellAt(p.x, p.y);
  view.classList.toggle('zoom-in', scale < MIN_PLACE_SCALE && !!cellAt(p.x, p.y));
}, { passive: false });

const center = () => ({ x: stage.clientWidth / 2, y: stage.clientHeight / 2 });
$('zoom-in').onclick = () => { const c = center(); zoomAt(1.5, c.x, c.y); };
$('zoom-out').onclick = () => { const c = center(); zoomAt(1 / 1.5, c.x, c.y); };
$('zoom-fit').onclick = fit;

window.addEventListener('keydown', e => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.key === '+' || e.key === '=') { const c = center(); zoomAt(1.5, c.x, c.y); }
  else if (e.key === '-') { const c = center(); zoomAt(1 / 1.5, c.x, c.y); }
  else if (e.key === '0') fit();
});

function showCoords() {
  $('coords').textContent = hover ? `x ${hover.x}  y ${hover.y}` : '';
}

let resizeTimer = 0;
window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = window.setTimeout(fit, 120); });

// ---------- Pixel setzen ----------

function place(x: number, y: number) {
  if (!ws || ws.readyState !== WebSocket.OPEN) { toast('Keine Verbindung zum Server'); return; }
  if (locked) { toast('Gleich kommt das nächste Bild'); return; }
  const i = y * W + x;
  if (board[i] === selected) return;
  board[i] = selected; // sofort zeigen, der Server bestätigt oder setzt zurück
  paint(x, y);
  pops.push({ x, y, c: selected, t: performance.now() });
  send({ t: 'px', x, y, c: selected });
  updateHintsCount();
}

// ---------- Farbpalette ----------

PALETTE.forEach((hex, i) => {
  const b = document.createElement('button');
  b.className = 'swatch';
  b.style.background = hex;
  b.title = `${PALETTE_NAMES[i]} (${hex})`;
  b.setAttribute('role', 'radio');
  b.setAttribute('aria-label', PALETTE_NAMES[i]);
  b.onclick = () => selectColor(i);
  paletteEl.append(b);
});
function selectColor(i: number) {
  selected = i;
  store.set('color', String(i));
  [...paletteEl.children].forEach((el, j) => el.setAttribute('aria-checked', String(j === i)));
}
selectColor(selected);

// ---------- Kreativ-Modus: Anzeige ----------

function showTarget() {
  goalCard.hidden = !target;
  if (!target) return;
  goalImg.width = target.w;
  goalImg.height = target.h;
  const g = goalImg.getContext('2d')!;
  g.clearRect(0, 0, target.w, target.h);
  target.cells.forEach((c, i) => {
    if (c === EMPTY) return;
    g.fillStyle = PALETTE[c];
    g.fillRect(i % target!.w, Math.floor(i / target!.w), 1, 1);
  });
  $('goal-name').textContent = target.name;
  $('goal-count').textContent = `Bild ${target.number} von ${target.count}`;
}

function showAccuracy(acc: number) {
  const pct = Math.floor(acc * 100);
  $('acc').textContent = `${pct}\u00a0%`;
  $('meter-fill').style.width = `${Math.min(100, acc * 100)}%`;
  goalCard.querySelector('.meter')!.classList.toggle('reached', acc >= GOAL);
}

// Nur für den Tooltip: wie viele Pixel noch fehlen
function updateHintsCount() {
  if (!target) return;
  let open = 0;
  target.cells.forEach((t, i) => { if (t !== EMPTY && board[i] !== t) open++; });
  goalCard.title = `Noch ${open} Pixel stimmen nicht`;
}

const hintsBox = $<HTMLInputElement>('hints');
hintsBox.checked = hints;
hintsBox.onchange = () => { hints = hintsBox.checked; store.set('hints', hints ? '1' : '0'); };

// ---------- Modus wechseln ----------

const tabs = [...document.querySelectorAll<HTMLButtonElement>('.tab')];
function showMode() {
  tabs.forEach(t => t.setAttribute('aria-selected', String(t.dataset.mode === mode)));
  document.title = mode === 'creative' ? 'Pixelwand · Kreativ-Modus' : 'Pixelwand';
}
function switchMode(m: Mode) {
  if (m === mode) return;
  mode = m;
  history.replaceState(null, '', m === 'creative' ? '#kreativ' : location.pathname);
  showMode();
  cursors.clear();
  refit = true;
  send({ t: 'mode', mode });
}
tabs.forEach(t => { t.onclick = () => switchMode(t.dataset.mode as Mode); });
window.addEventListener('hashchange', () => switchMode(modeFromHash()));
showMode();

nameInput.addEventListener('change', () => {
  myName = nameInput.value.trim().slice(0, 20) || myName;
  nameInput.value = myName;
  store.set('name', myName);
  send({ t: 'name', name: myName });
});
nameInput.addEventListener('keydown', e => { if (e.key === 'Enter') nameInput.blur(); });

// ---------- Meldungen ----------

let toastTimer = 0;
function toast(text: string, good = false, ms = 2200) {
  toastEl.textContent = text;
  toastEl.classList.toggle('good', good);
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toastEl.hidden = true; }, ms);
}

// ---------- Verbindung ----------

let ws: WebSocket | null = null;
let retry = 0;
let refit = true;
let lastCursor = 0;
let cursorTimer = 0;
let pendingCursor: [number, number] | null = null;

function send(m: ClientMsg) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
}

// Zeiger höchstens alle 50 ms schicken, die letzte Position aber nie verlieren
function sendCursor(x: number, y: number) {
  pendingCursor = [x, y];
  const wait = 50 - (performance.now() - lastCursor);
  if (wait <= 0) flushCursor();
  else if (!cursorTimer) cursorTimer = window.setTimeout(flushCursor, wait);
}
function flushCursor() {
  cursorTimer = 0;
  if (!pendingCursor) return;
  lastCursor = performance.now();
  const [x, y] = pendingCursor;
  pendingCursor = null;
  send({ t: 'cur', x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
}

function connect() {
  const conn = $('conn');
  ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`);
  ws.binaryType = 'arraybuffer';
  ws.onopen = () => {
    retry = 0;
    conn.hidden = true;
    send({ t: 'hello', name: myName, mode });
  };
  ws.onmessage = e => {
    if (e.data instanceof ArrayBuffer) {
      board = new Uint8Array(e.data);
      paintAll();
      if (refit) { fit(); refit = false; }
      updateHintsCount();
      return;
    }
    handle(JSON.parse(e.data) as ServerMsg);
  };
  ws.onclose = () => {
    cursors.clear();
    conn.hidden = false;
    window.setTimeout(connect, Math.min(8000, 500 * 2 ** retry++));
  };
}

function handle(m: ServerMsg) {
  switch (m.t) {
    case 'init': {
      // Neue Grösse oder anderer Modus: nach dem Eintreffen der Leinwand neu einpassen
      if (m.w !== W || m.h !== H || m.mode !== mode || !board.length) refit = true;
      mode = m.mode;
      me = m.you;
      W = m.w;
      H = m.h;
      target = m.target;
      locked = false;
      showMode();
      showTarget();
      showAccuracy(m.acc);
      $('solved').textContent = String(m.solved);
      break;
    }
    case 'px':
      if (m.by === me) break; // schon selbst gesetzt
      if (m.x >= W || m.y >= H) break;
      board[m.y * W + m.x] = m.c;
      paint(m.x, m.y);
      if (m.by) pops.push({ x: m.x, y: m.y, c: m.c, t: performance.now() });
      updateHintsCount();
      break;
    case 'curs':
      for (const [id, name, color, x, y] of m.list) {
        const c = cursors.get(id);
        if (c) Object.assign(c, { name, color, tx: x, ty: y });
        else cursors.set(id, { name, color, x, y, tx: x, ty: y });
      }
      break;
    case 'left':
      cursors.delete(m.id);
      break;
    case 'acc':
      showAccuracy(m.acc);
      break;
    case 'done':
      locked = true;
      showAccuracy(m.acc);
      toast(`Geschafft: ${Math.floor(m.acc * 100)}\u00a0% richtig! Als Nächstes: ${m.next}`, true, m.inMs);
      break;
    case 'online':
      document.querySelectorAll<HTMLElement>('[data-count]').forEach(el => {
        el.textContent = String(m[el.dataset.count as Mode]);
      });
      break;
    case 'err':
      toast(m.msg);
      break;
  }
}

connect();
